/**
 * screens/admin/adminOverview.tsx — Platform Admin overview (staff KPIs).
 * Faithful RN port of design/admin.jsx AdminOverview: MRR / active orgs / live
 * apps / published plans stats + application & plan summary panels.
 */

import React from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useShellNav } from '../../navigation/shellNav';
import { SettingsHeader, Panel, StatusBadge } from '../../components/platform/primitives';
import { PButton, Stat } from '../../components/platform/controls';
import { AppIcon } from './adminShared';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, inr } from '../../utils/platformTokens';

const AdminOverview: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, isPlatformAdmin } = usePlatform();
  const { goApp } = useShellNav();
  if (!isPlatformAdmin) return <PermissionDenied />;

  const mrr = state.tenants.reduce((s, t) => s + t.mrr, 0);
  const active = state.tenants.filter((t) => t.status === 'active').length;
  const liveApps = state.applications.filter((a) => a.status === 'active').length;

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Admin Console" sub="Manage the application catalog, plans and customer organizations across the platform." />

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginBottom: 24 }}>
        <Stat label="Monthly recurring revenue" value={inr(mrr)} sub={`${state.tenants.length} organizations`} />
        <Stat label="Active organizations" value={active} sub={`${state.tenants.length - active} trial / past-due / suspended`} />
        <Stat label="Live applications" value={liveApps} sub={`${state.applications.length} in catalog`} />
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
        <View style={{ flex: 1, minWidth: 300 }}>
          <Panel title="Applications" sub="The product modules tenants can subscribe to." style={{ marginBottom: 0 }}
            actions={<PButton size="sm" onPress={() => goApp('admin', 'admin-applications')}>Manage</PButton>}>
            <View style={{ gap: 8 }}>
              {state.applications.slice(0, 4).map((a) => (
                <View key={a.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <AppIcon icon={a.icon} size={28} />
                    <Text style={{ fontWeight: FW.medium, color: c.ink }}>{a.name}</Text>
                  </View>
                  <StatusBadge status={a.status} />
                </View>
              ))}
            </View>
          </Panel>
        </View>
        <View style={{ flex: 1, minWidth: 300 }}>
          <Panel title="Plan catalog" sub="Plans, bundles, grants, features and embed registrations." style={{ marginBottom: 0 }}
            actions={<PButton size="sm" onPress={() => goApp('admin', 'admin-catalog')}>Manage</PButton>}>
            <Text style={{ color: c.ink3, fontSize: FS.md }}>Prices live in Billing; each plan points to a Billing price.</Text>
          </Panel>
        </View>
      </View>
    </ScrollView>
  );
};

export default AdminOverview;
